import { t, Instance, flow } from 'mobx-state-tree';
import { PersonFactModel, createPersonFactModelFromResponse } from './models/PersonFact';
import { personFactsApi } from '@/api';
import type { PersonFactResponse, CreatePersonFactRequest, UpdatePersonFactRequest } from 'we-tree-api-client/v2';

/**
 * PersonFactsStore - Manages person facts
 * Stores facts keyed by familyMemberId for efficient lookup
 */
export const PersonFactsStore = t
  .model('PersonFactsStore', {
    // Map of familyMemberId -> Map of factId -> PersonFactModel
    factsByFamilyMember: t.map(t.map(PersonFactModel)),
    // Loading states per family member
    loadingStates: t.map(t.boolean),
    // Errors per family member
    errors: t.map(t.string)
  })
  .views((self) => ({
    /**
     * Get all facts for a specific family member
     */
    getFactsForFamilyMember(familyMemberId: string): Instance<typeof PersonFactModel>[] {
      const factsMap = self.factsByFamilyMember.get(familyMemberId);
      if (!factsMap) {
        return [];
      }
      return Array.from(factsMap.values());
    },

    /**
     * Get a specific fact by ID
     */
    getFactById(factId: string): Instance<typeof PersonFactModel> | undefined {
      for (const factsMap of self.factsByFamilyMember.values()) {
        const fact = factsMap.get(factId);
        if (fact) {
          return fact;
        }
      }
      return undefined;
    },

    /**
     * Check if facts are loading for a family member
     */
    isLoadingForFamilyMember(familyMemberId: string): boolean {
      return self.loadingStates.get(familyMemberId) || false;
    },

    /**
     * Get error for a family member
     */
    getErrorForFamilyMember(familyMemberId: string): string | null {
      return self.errors.get(familyMemberId) || null;
    }
  }))
  .actions((self) => ({
    /**
     * Clear facts for a specific family member
     */
    clearFactsForFamilyMember(familyMemberId: string) {
      self.factsByFamilyMember.delete(familyMemberId);
      self.loadingStates.delete(familyMemberId);
      self.errors.delete(familyMemberId);
    },

    /**
     * Clear all facts
     */
    clear() {
      self.factsByFamilyMember.clear();
      self.loadingStates.clear();
      self.errors.clear();
    },

    /**
     * Set loading state for a family member
     */
    setLoading(familyMemberId: string, isLoading: boolean) {
      if (isLoading) {
        self.loadingStates.set(familyMemberId, true);
      } else {
        self.loadingStates.delete(familyMemberId);
      }
    },

    /**
     * Set error for a family member
     */
    setError(familyMemberId: string, errorMessage: string | null) {
      if (errorMessage) {
        self.errors.set(familyMemberId, errorMessage);
      } else {
        self.errors.delete(familyMemberId);
      }
    },

    /**
     * Load facts for a family member from the API
     */
    loadFactsForFamilyMember: flow(function* (familyMemberId: string) {
      self.loadingStates.set(familyMemberId, true);
      self.errors.delete(familyMemberId);

      try {
        const result = yield personFactsApi.listPersonFacts(familyMemberId);

        if (result.success) {
          const facts = result.data.facts || [];

          // Get or create the facts map for this family member
          let factsMap = self.factsByFamilyMember.get(familyMemberId);
          if (!factsMap) {
            // Initialize with empty snapshot - MST will create the map
            const emptyMapSnapshot: Record<string, never> = {};
            self.factsByFamilyMember.set(familyMemberId, emptyMapSnapshot);
            factsMap = self.factsByFamilyMember.get(familyMemberId)!;
          } else {
            factsMap.clear();
          }

          // Add facts to the map
          facts.forEach((fact: PersonFactResponse) => {
            try {
              const factModel = createPersonFactModelFromResponse(fact);
              factsMap.set(fact.id, factModel);
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error('[PersonFactsStore] Failed to create fact model:', {
                fact,
                error: error instanceof Error ? error.message : String(error)
              });
            }
          });

          self.errors.delete(familyMemberId);
        } else {
          const errorMessage = result.error?.getUserMessage() || 'Failed to load person facts';
          self.errors.set(familyMemberId, errorMessage);
          // eslint-disable-next-line no-console
          console.error('[PersonFactsStore] Failed to load facts:', errorMessage, result.error);
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
        self.errors.set(familyMemberId, errorMessage);
        // eslint-disable-next-line no-console
        console.error('[PersonFactsStore] Error loading facts:', error);
      } finally {
        self.loadingStates.delete(familyMemberId);
      }
    }),

    /**
     * Create a new person fact
     */
    createPersonFact: flow(function* (request: CreatePersonFactRequest) {
      try {
        const result = yield personFactsApi.createPersonFact(request);
        if (result.success && result.data) {
          // Add the new fact to the store
          let factsMap = self.factsByFamilyMember.get(request.familyMemberId);
          if (!factsMap) {
            // Initialize with empty snapshot - MST will create the map
            const emptyMapSnapshot: Record<string, never> = {};
            self.factsByFamilyMember.set(request.familyMemberId, emptyMapSnapshot);
            factsMap = self.factsByFamilyMember.get(request.familyMemberId)!;
          }
          const factModel = createPersonFactModelFromResponse(result.data);
          factsMap.set(result.data.id, factModel);
          return { success: true, data: result.data };
        } else {
          return { success: false, error: result.error };
        }
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error : new Error('Failed to create person fact')
        };
      }
    }),

    /**
     * Update an existing person fact
     */
    updatePersonFact: flow(function* (factId: string, request: UpdatePersonFactRequest) {
      try {
        const result = yield personFactsApi.updatePersonFact(factId, request);
        if (result.success && result.data) {
          // Update the fact in the store
          const fact = self.getFactById(factId);
          if (fact) {
            const factsMap = self.factsByFamilyMember.get(fact.familyMemberId);
            if (factsMap) {
              const updatedFactModel = createPersonFactModelFromResponse(result.data);
              factsMap.set(factId, updatedFactModel);
            }
          }
          return { success: true, data: result.data };
        } else {
          return { success: false, error: result.error };
        }
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error : new Error('Failed to update person fact')
        };
      }
    }),

    /**
     * Delete a person fact
     */
    deletePersonFact: flow(function* (factId: string) {
      try {
        const fact = self.getFactById(factId);
        if (!fact) {
          return {
            success: false,
            error: new Error('Fact not found in store')
          };
        }

        const result = yield personFactsApi.deletePersonFact(factId);
        if (result.success) {
          // Remove from store
          const factsMap = self.factsByFamilyMember.get(fact.familyMemberId);
          if (factsMap) {
            factsMap.delete(factId);
          }
          return { success: true };
        } else {
          return { success: false, error: result.error };
        }
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error : new Error('Failed to delete person fact')
        };
      }
    })
  }));

export type PersonFactsStoreType = Instance<typeof PersonFactsStore>;

// Export singleton instance
export const personFactsStore = PersonFactsStore.create({});
